import React, { useState, useEffect } from 'react';

import { Button, InputNumber, Input, Form, Space, Select, Switch, Modal, notification } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';

import IBaseComponentProps from '@components/base-component-props';

import { userLoaded } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { getEnumList } from '@extensions/utils';

import { exception, error } from '@extensions/notification';
import { serverFetch } from '@flp/server';

import { IUserSession } from '@entities/user-session';
import { IWaypoint } from '@entities/waypoint';
import { IWarehouse } from '@entities/warehouse';

import { WarehouseType } from '@enums/warehouse-type';
import { Permission, hasPermission } from '@enums/permission';
import { WaypointType, enumLabel as waypointTypeLabel } from '@enums/waypoint-type';
import { RouteZone, enumLabel as routeZoneLabel } from '@enums/route-zone';

interface IWaypointProps extends IBaseComponentProps {
    headerId?: string;
    waypointId?: string;
    routeZone: RouteZone;
    onSave: () => void;
    onCancel: () => void;
    api: NotificationInstance;
}

const Waypoint = (props: IWaypointProps) => {
    const { waypointId, routeZone, headerId, onSave, onCancel, api } = props;

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [form] = Form.useForm();

    const [entity, setEntity] = useState<IWaypoint>();

    const [warehouses, setWarehouses] = useState<Array<IWarehouse>>([]);
    const [waypointTypes] = useState(getEnumList(WaypointType, waypointTypeLabel));
    const [processing, setProcessing] = useState<boolean>(false);

    useEffect(() => {
        serverFetch('warehouses', { method: 'GET', queryParams: { type: WarehouseType.Main } })
            .then((data) => {
                setWarehouses(data);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения складов', ex, () => d(userLoaded(undefined)));
            });
    }, []);

    useEffect(() => {
        if (waypointId) {
            serverFetch(`waypoints/${waypointId}`, { method: 'GET' })
                .then((data) => {
                    setEntity(data);
                })
                .catch((ex) => {
                    exception(api, 'Ошибка получения маршрутной точки', ex, () => d(userLoaded(undefined)));
                });
        } else {
            setEntity({
                id: undefined,
                headerId: headerId,
                routeZone: routeZone,
                type: undefined,
                sequence: 0,
                name: undefined,
                allowAcceptance: false,
                archived: false,
                warehouseId: undefined,
            });
        }
    }, [waypointId]);

    const onFinish = () => {
        setProcessing(true);

        serverFetch(`waypoints`, { method: waypointId ? 'PUT' : 'POST', bodyData: entity })
            .then(() => {
                onSave();
                setProcessing(false);
            })
            .catch((ex) => {
                setProcessing(false);
                exception(api, 'Ошибка сохранения маршрутной точки', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        entity && (
            <Modal
                destroyOnHidden={true}
                width={550}
                title={`${waypointId ? 'Изменить маршрутную точку' : 'Добавить маршрутную точку'}`}
                open={true}
                okText='ОК'
                cancelButtonProps={{ disabled: processing }}
                okButtonProps={{ loading: processing, disabled: processing }}
                onOk={() => {
                    form.submit();
                }}
                onCancel={() => onCancel()}
            >
                <Form colon={false} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} form={form} onFinish={onFinish}>
                    <Form.Item
                        initialValue={entity.type}
                        label='Тип'
                        name='type'
                        required
                        rules={[{ required: true, message: 'Укажите тип точки' }]}
                    >
                        <Select
                            allowClear
                            onChange={(value) => {
                                setEntity({ ...entity, type: value });
                            }}
                            options={waypointTypes.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />
                    </Form.Item>
                    <Form.Item initialValue={entity.warehouseId} label='Склад' name='warehouseId'>
                        <Select
                            allowClear
                            onChange={(value) => {
                                setEntity({ ...entity, warehouseId: value });
                            }}
                            options={warehouses.map((t) => {
                                return { value: t.id, label: t.code };
                            })}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity.sequence}
                        label='Порядковый номер'
                        name='sequence'
                        required
                        rules={[{ required: true, message: 'Укажите порядковый номер' }]}
                    >
                        <InputNumber
                            onChange={(value: number | null) => {
                                setEntity({ ...entity, sequence: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label='Приемка груза' name='allowAcceptance' valuePropName='checked'>
                        <Switch
                            defaultChecked={entity?.allowAcceptance}
                            onChange={(value) => {
                                setEntity({ ...entity, allowAcceptance: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item initialValue={entity.name} label='Название' name='name'>
                        <Input
                            autoFocus
                            onChange={(data) => {
                                setEntity({ ...entity, name: data.target.value });
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        )
    );
};

export default Waypoint;
